import { ManfrediteOfferFilterCreateRequest } from '@asgard/api-common';
import { MultilingualLanguages, RemoteOptions } from '@asgard/common-dto';
import { OffersListFilter } from 'model/model-offers-list-filter';
import { LanguageLevelToCommonDtoLevelDict } from 'services/service.language.normalizer';
import { getLocale } from 'utils/localize.utils';

// Public methods
// ----------------------------------------------------------------------------
export function normalizeToOfferAlertCreateRequest(
  filter: OffersListFilter,
  lang: MultilingualLanguages
): ManfrediteOfferFilterCreateRequest {
  return {
    currency: getCurrencyFieldFromFilter(filter),
    remoteOptions: getRemoteOptionsFieldFromFilter(filter),
    locations: getLocationsFieldFromFilter(filter),
    techs: getTechsFieldFromFilter(filter),
    techRoles: getTechRolesFieldFromFilter(filter),
    offerLanguage: getOfferLanguageFieldFromFilter(filter),
    minSalary: getMinSalaryFieldFromFilter(filter),
    text: getTextFieldFromFilter(filter),
    minLanguages: getMinLanguagesFieldFromFilter(filter),
    lang
  };
}

// Private functions
// ----------------------------------------------------------------------------
function getCurrencyFieldFromFilter(
  filter: OffersListFilter
): ManfrediteOfferFilterCreateRequest['currency'] {
  return filter.currency || '€';
}

function getRemoteOptionsFieldFromFilter(
  filter: OffersListFilter
): ManfrediteOfferFilterCreateRequest['remoteOptions'] {
  const options = filter.remoteOptions?.filter(Boolean) || [];

  return options.length > 0 ? (options as RemoteOptions[]) : [];
}

function getMinLanguagesFieldFromFilter(
  filter: OffersListFilter
): ManfrediteOfferFilterCreateRequest['minLanguages'] {
  const minLangs: ManfrediteOfferFilterCreateRequest['minLanguages'] =
    filter.minLanguages?.filter(Boolean).map((minLang) => ({
      languageId: minLang.language.value,
      level: LanguageLevelToCommonDtoLevelDict[minLang.level.value]
    })) || [];

  return minLangs.length > 0 ? minLangs : [];
}

function getLocationsFieldFromFilter(
  filter: OffersListFilter
): ManfrediteOfferFilterCreateRequest['locations'] {
  const locations =
    filter.locations?.map((c) => c?.placeId || '').filter((c) => !!c) || [];

  return locations.length > 0 ? locations : [];
}

function getTechsFieldFromFilter(
  filter: OffersListFilter
): ManfrediteOfferFilterCreateRequest['techs'] {
  const techs = filter.techs?.filter(Boolean) || [];

  return techs.length > 0 ? techs : [];
}

function getTechRolesFieldFromFilter(
  filter: OffersListFilter
): ManfrediteOfferFilterCreateRequest['techRoles'] {
  const techRoles = filter.techRoles?.filter(Boolean) || [];

  return techRoles.length > 0 ? techRoles : [];
}

function getOfferLanguageFieldFromFilter(
  filter: OffersListFilter
): ManfrediteOfferFilterCreateRequest['offerLanguage'] {
  return filter.offerLanguage ? getLocale(filter.offerLanguage) : undefined;
}

function getMinSalaryFieldFromFilter(
  filter: OffersListFilter
): ManfrediteOfferFilterCreateRequest['minSalary'] {
  return filter.minSalary ? +filter.minSalary : undefined;
}

function getTextFieldFromFilter(
  filter: OffersListFilter
): ManfrediteOfferFilterCreateRequest['text'] {
  return filter.text || undefined;
}
